import { Close, People } from '@mui/icons-material';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  darken,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import Fuse from 'fuse.js';
import tenant from '@/services/TenantService';

/**
 * Party Selector Component
 * A unified dropdown to select ORGANIZATIONs or PERSONs
 * @param {*} param0
 * @returns
 */
export default function UserSelector({ user, setUser, options }) {
  const theme = useTheme();
  const [edit, setEdit] = useState(false);
  const [users, setUsers] = useState(options || []);
  const [loading, setLoading] = useState(false);
  const fuse = new Fuse(users, {
    keys: ['firstName', 'lastName', 'email'],
    threshold: 0.3,
  });

  useEffect(() => {
    if (options) return;
    (async () => {
      setLoading(true);
      const results = await tenant.get(`/user`);
      setUsers(results.data);
      setLoading(false);
    })();
  }, [options]);

  const title = user ? `${user.firstName} ${user.lastName}` : 'Benutzer auswählen';
  const subtitle = user ? user.email : 'Klicken zum Auswählen';

  async function search(query) {
    setLoading(true);
    const result = fuse.search(query).map((res) => res.item);
    setUsers(result);
    setLoading(false);
  }

  return (
    <Card
      sx={{
        p: 2,
        width: '100%',
        height: '60px',
        boxShadow: 'none',
        backgroundColor: darken(theme.palette.background.paper, 0.03),
        cursor: !edit && 'pointer',
        userSelect: 'none',
        display: 'flex',
        flexDirection: 'row',
        gap: 1.5,
        alignItems: 'center',
        '&:hover': {
          backgroundColor:
            !edit && darken(theme.palette.background.paper, 0.06),
        },
        '&:active': {
          backgroundColor:
            !edit && darken(theme.palette.background.paper, 0.09),
        },
        overflow: 'hidden',
      }}
      onClick={() => !edit && setEdit(true)}
    >
      {!edit && (
        <>
          <Avatar
            src={user?.profilePictureUrl}
            sx={{
              width: 35,
              height: 35,
              '& img': !user?.profilePictureUrl && {
                objectFit: 'contain',
                width: '80%',
              },
              backgroundColor: theme.palette.background.default,
            }}
          >
            <People sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          </Avatar>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: 'subtitle1.fontSize',
                lineHeight: 1.2,
                maxWidth: '90%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={title}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.2,
                maxWidth: '90%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={subtitle}
            >
              {subtitle}
            </Typography>
          </Box>
          {user && (
            <Button
              onClick={(evt) => {
                evt.stopPropagation();
                setUser(null);
              }}
              size="small"
              sx={{
                minWidth: '30px',
                width: '30px',
                height: '30px',
                padding: 0,
                fontSize: '16px',
              }}
            >
              <Close />
            </Button>
          )}
        </>
      )}
      {edit && (
        <Autocomplete
          open={edit}
          onOpen={() => setEdit(true)}
          noOptionsText="Suchbegriff eingeben..."
          onClose={(event, reason) => {
            // prevent closing when clicking in the input
            if (['blur', 'escape', 'selectOption'].includes(reason))
              setEdit(false);
          }}
          onChange={(evt, value) => setUser(value)}
          options={users || []}
          getOptionLabel={(option) =>
            option.partyType === 'ORGANIZATION'
              ? option.company
              : `${option.firstName} ${option.lastName}`
          }
          renderOption={(props, option) => (
            <ListItem {...props} key={option._id}>
              <ListItemText
                primary={
                  option.partyType === 'ORGANIZATION'
                    ? option.company
                    : `${option.firstName} ${option.lastName}`
                }
                secondary={
                  option.customerNumber || option.supplierNumber || option.email
                }
                sx={{
                  '& .MuiListItemText-primary': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiListItemText-secondary': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </ListItem>
          )}
          filterOptions={(x) => x}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Party Name"
              autoFocus
              sx={{
                '& .MuiInputBase-input': { p: 1.5 },
                '& .MuiOutlinedInput-root': { pr: '16px !important' },
              }}
              fullWidth
              onChange={(evt) => search(evt.target.value)}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      )}
    </Card>
  );
}
